import { useEffect, useRef, useState } from 'react';

// Mirrors the `@container` breakpoint in TablePaginationBar.module.css (keep
// in sync): below this width prev/next shrink from 150px to 40px icon-only
// buttons, which is what the width budget below assumes.
const WORDS_HIDDEN_AT = 580;

// A page-number/dots button is ~30-40px depending on digit count (per design
// review); use the worst case so the fit check never underestimates and
// ends up clipping a button instead of collapsing to a smaller maxFirst.
const BUTTON_WIDTH = 40;
const PREV_NEXT_WIDTH_WITH_WORDS = 150;
const PREV_NEXT_WIDTH_ICON_ONLY = 40;

// The widest cluster Pagination ever renders is maxFirst numbers plus one
// "..." plus the last-page button (see the near-page-1 case in
// Pagination.jsx), so that's the budget that has to fit next to prev/next.
// maxFirst === 0 is the floor: Pagination renders only the current page,
// no dots, no last-page button, so it only needs a single button's width.
function widthNeededFor(maxFirst: number, prevNextWidth: number) {
  const items = maxFirst === 0 ? 1 : maxFirst + 2;
  return prevNextWidth * 2 + items * BUTTON_WIDTH;
}

const MAX_FIRST_STEPS = [5, 3, 1, 0];

function pickMaxFirst(containerWidth: number) {
  const prevNextWidth = containerWidth >= WORDS_HIDDEN_AT ? PREV_NEXT_WIDTH_WITH_WORDS : PREV_NEXT_WIDTH_ICON_ONLY;
  for (const maxFirst of MAX_FIRST_STEPS) {
    if (containerWidth >= widthNeededFor(maxFirst, prevNextWidth)) return maxFirst;
  }
  return MAX_FIRST_STEPS[MAX_FIRST_STEPS.length - 1];
}

/**
 * Tracks the pagination bar's own width and derives how many leading page
 * numbers (`maxFirst`) it can show without overflowing. Word-hiding is
 * handled separately by a CSS container query — this only decides the
 * step CSS can't: how many page-number nodes to render at all.
 */
export function usePaginationFit<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [maxFirst, setMaxFirst] = useState(5);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== 'function') return;

    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = pickMaxFirst(entry.contentRect.width);
        setMaxFirst(prev => (prev === next ? prev : next));
      });
    });
    observer.observe(el);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return { ref, maxFirst };
}
